import type { SubwayStationArrival, SubwayTravelDirection } from '@vitals/shared';
import { countdownText } from './tooltip.js';
import { bulletTextColor } from '../layers/bullets.js';
import { routeBulletPresentation } from '../routeBulletPresentation.js';
import type { PickingInfo } from '@deck.gl/core';

export interface StationCardArrival extends SubwayStationArrival {
  countdown: string;
}

export interface StationCardSection {
  direction: SubwayTravelDirection;
  arrivals: StationCardArrival[];
}

const oppositeDirection: Record<SubwayTravelDirection, SubwayTravelDirection> = {
  Northbound: 'Southbound', Southbound: 'Northbound', Eastbound: 'Westbound', Westbound: 'Eastbound',
};

/** Group station arrivals for the lightweight hover card. */
export function stationCardSections(
  arrivals: SubwayStationArrival[],
  nowMs: number,
  stationAxes: SubwayTravelDirection[] = [],
): StationCardSection[] {
  const directions = arrivals.flatMap((arrival) => [arrival.direction, oppositeDirection[arrival.direction]]);
  const visibleDirections = [...new Set([...stationAxes, ...directions])];
  return visibleDirections.map((direction) => ({
    direction,
    arrivals: arrivals
      .filter((arrival) => arrival.direction === direction && arrival.arrivalAt > nowMs)
      .sort((a, b) => a.arrivalAt - b.arrivalAt)
      .slice(0, 6)
      .map((arrival) => ({ ...arrival, countdown: countdownText(arrival.arrivalAt, nowMs)! })),
  }));
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);
}

export function cardPosition(x: number, y: number, width: number, height: number, viewportWidth: number, viewportHeight: number): { left: number; top: number } {
  return { left: Math.max(0, Math.min(x + 12, viewportWidth - width)), top: Math.max(0, Math.min(y + 12, viewportHeight - height)) };
}

/** A hover-only station overlay which remains readable after leaving the marker. */
export function createStationHoverCard(options: {
  root: HTMLElement;
  nowMs: () => number;
  arrivalsFor: (selectionId: string) => SubwayStationArrival[];
  axesFor: (selectionId: string) => SubwayTravelDirection[];
}): { handlePick: (info: PickingInfo) => void; destroy: () => void } {
  const el = document.createElement('div');
  el.className = 'subway-station-card';
  el.style.cssText = 'display:none;position:fixed;z-index:4;min-width:220px;max-width:300px;max-height:calc(100vh - 16px);overflow-y:auto;padding:9px 10px;border-radius:9px;background:rgba(8,12,20,.96);color:#e8eefc;box-shadow:0 4px 16px rgba(0,0,0,.5);font:13px/1.3 system-ui,sans-serif;pointer-events:auto;box-sizing:border-box';
  options.root.append(el);
  let selectionId: string | null = null;
  let name = '';
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let tickTimer: ReturnType<typeof setInterval> | undefined;
  let anchor: { x: number; y: number } | null = null;
  // Identity of the last rendered arrival set (excludes the countdown text
  // itself, which changes every tick) plus the countdown nodes it produced —
  // lets the 1Hz tick update text in place instead of rebuilding the whole
  // card, which would otherwise reset scroll position and drop any
  // in-progress text selection.
  let lastArrivalsKey: string | null = null;
  let countdownEls: HTMLElement[] = [];
  const arrivalKey = (arrival: StationCardArrival): string =>
    `${arrival.routeId}|${arrival.direction}|${arrival.destination}|${arrival.arrivalAt}`;
  const cancelHide = () => {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = undefined;
  };
  const hide = () => {
    selectionId = null;
    el.style.display = 'none';
    if (tickTimer) clearInterval(tickTimer);
    tickTimer = undefined;
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer = setTimeout(hide, 100);
  };
  const render = () => {
    if (!selectionId) return;
    const sections = stationCardSections(options.arrivalsFor(selectionId), options.nowMs(), options.axesFor(selectionId));
    const flatArrivals = sections.flatMap((section) => section.arrivals);
    // Include selectionId so switching to a different station whose arrivals
    // happen to produce the same key (e.g. two stations both showing no
    // arrivals) still forces a full rebuild of the header/section labels.
    const key = `${selectionId}::${flatArrivals.map(arrivalKey).join(',')}`;
    if (key === lastArrivalsKey && countdownEls.length === flatArrivals.length) {
      // Cheap path: the arrival set itself hasn't changed, only elapsed time
      // has — update countdown text nodes in place via textContent (never
      // innerHTML, so no interpolated feed text can execute as markup) rather
      // than rebuilding the card and yanking the user's scroll position or
      // dropping an in-progress selection.
      flatArrivals.forEach((arrival, i) => {
        countdownEls[i]!.textContent = arrival.countdown;
      });
      return;
    }
    lastArrivalsKey = key;
    el.innerHTML = `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(name)}</div>${sections.map((section) => `<section style="margin-top:6px"><div style="font-size:11px;opacity:.7;margin-bottom:2px">${section.direction}</div>${section.arrivals.length ? section.arrivals.map((arrival) => {
      const presentation = routeBulletPresentation(arrival.routeId);
      // A 16px circle can't legibly render a true diamond outline; approximate
      // the map's express indicator with a squarer corner radius instead of
      // reproducing the raw (sometimes non-canonical) route id as text.
      const borderRadius = presentation.shape === 'diamond' ? '4px' : '50%';
      return `<div style="display:flex;align-items:center;gap:6px;min-height:20px"><span style="display:inline-grid;place-items:center;width:16px;height:16px;border-radius:${borderRadius};background:rgb(${arrival.color.join(',')});color:${bulletTextColor(arrival.routeId)};font-size:10px;font-weight:700">${escapeHtml(presentation.label)}</span><span style="flex:1">to ${escapeHtml(arrival.destination)}</span><span class="arrival-countdown" style="opacity:.8">${arrival.countdown}</span></div>`;
    }).join('') : '<div style="opacity:.65">No arrivals available</div>'}</section>`).join('')}`;
    countdownEls = [...el.querySelectorAll<HTMLElement>('.arrival-countdown')];
    if (anchor) {
      const rect = el.getBoundingClientRect();
      const position = cardPosition(anchor.x, anchor.y, rect.width, rect.height, window.innerWidth, window.innerHeight);
      el.style.left = `${position.left}px`;
      el.style.top = `${position.top}px`;
    }
  };
  el.addEventListener('pointerenter', cancelHide);
  el.addEventListener('pointerleave', scheduleHide);
  return {
    handlePick(info: PickingInfo): void {
      if (info.layer?.id !== 'subwayStations' || !info.object) {
        scheduleHide();
        return;
      }
      const properties = (info.object as { properties?: { selectionId?: unknown; name?: unknown } }).properties;
      if (typeof properties?.selectionId !== 'string' || typeof properties.name !== 'string') {
        scheduleHide();
        return;
      }
      cancelHide();
      selectionId = properties.selectionId;
      name = properties.name;
      anchor = { x: info.x, y: info.y };
      el.style.display = 'block';
      render();
      if (!tickTimer) tickTimer = setInterval(render, 1_000);
    },
    destroy(): void {
      cancelHide();
      if (tickTimer) clearInterval(tickTimer);
      el.remove();
    },
  };
}
